import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, ThumbsUp, ArrowRight, Users, Film, Tv, Lightbulb, Swords, Hash, Globe } from "lucide-react";

const TAG_ICON = {
  movies:         { icon: Film,        color: "text-[#007AFF]",    bg: "bg-[#007AFF]/12" },
  tv:             { icon: Tv,          color: "text-emerald-400",  bg: "bg-emerald-500/12" },
  recommendation: { icon: Lightbulb,   color: "text-amber-400",    bg: "bg-amber-500/12" },
  debate:         { icon: Swords,      color: "text-[#E50914]",    bg: "bg-[#E50914]/12" },
  general:        { icon: Hash,        color: "text-neutral-400",  bg: "bg-white/8" },
};

const TAG_LABEL = { movies: "Movies", tv: "TV Shows", recommendation: "Recs", debate: "Debate", general: "General" };

export default function HomeSocialWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/social/trending-discussions?limit=3`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setItems(d.items); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  // Don't render if no data
  if (!loading && items.length === 0) return null;

  const handleCardClick = (id) => {
    navigate(`/social?tab=discussions&thread=${id}`);
  };

  return (
    <section
      className="px-4 sm:px-6 lg:px-10 py-8 mt-4"
      data-testid="home-social-widget"
    >
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-1 h-5 bg-[#E50914] rounded-full" />
            <h2 className="font-display text-base sm:text-lg font-bold tracking-tight text-white flex items-center gap-2">
              <Users className="w-4 h-4 text-[#E50914]" />
              Trending in Community
            </h2>
          </div>
          <Link
            to="/social"
            data-testid="social-widget-view-all"
            className="flex items-center gap-1 text-xs text-neutral-500 hover:text-white transition-colors group"
          >
            Join the discussion
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {items.map((item) => {
              const tm = TAG_ICON[item.tag] || TAG_ICON.general;
              const Icon = tm.icon;
              return (
                <button
                  key={item.id}
                  data-testid={`social-widget-card-${item.id}`}
                  onClick={() => handleCardClick(item.id)}
                  className="group text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] hover:border-white/[0.12] rounded-xl p-3.5 transition-all duration-200"
                >
                  {/* Tag chip */}
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${tm.bg} ${tm.color} mb-2`}>
                    <Icon className="w-2.5 h-2.5" />
                    {TAG_LABEL[item.tag] || "General"}
                  </span>

                  {/* Title */}
                  <p className="text-xs font-semibold text-white leading-snug line-clamp-2 group-hover:text-[#E50914]/90 transition-colors mb-2">
                    {item.title}
                  </p>

                  {/* Meta row */}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-neutral-600 truncate max-w-[100px]">{item.user_name}</span>
                    <div className="flex items-center gap-2.5 text-[10px] text-neutral-600">
                      <span className="flex items-center gap-0.5">
                        <ThumbsUp className="w-2.5 h-2.5" /> {item.like_count}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <MessageSquare className="w-2.5 h-2.5" /> {item.comment_count}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
